import { useSyncExternalStore } from "react";

const listeners = new Set();
let state = { status: "idle", kg: 0, stable: false, tare: 0, error: "", sim: false, raw: [] };
let port = null;
let reader = null;
let pollTimer = null;
let simTimer = null;
let history = [];
let sawUnit = false;
const SPLIT = /[\r\n\x02\x03]+/;
const unesc = (s) => s.replace(/\\r/g, "\r").replace(/\\n/g, "\n");

// Typical settings; always confirm with the scale's manual.
export const PRESETS = [
  { key: "generic", label: "Generic 9600 8N1", baud: 9600, dataBits: 8, parity: "none", poll: "" },
  { key: "cas", label: "CAS (continuous)", baud: 9600, dataBits: 8, parity: "none", poll: "" },
  { key: "mt_sics", label: "Mettler Toledo MT-SICS", baud: 9600, dataBits: 8, parity: "none", poll: "S\\r\\n" },
  { key: "toledo8217", label: "Toledo 8217 / Dibal / Epelsa", baud: 9600, dataBits: 7, parity: "even", poll: "W" },
  { key: "nci", label: "Avery Berkel / NCI", baud: 9600, dataBits: 7, parity: "even", poll: "W\\r" },
];

const emit = (patch) => { state = { ...state, ...patch }; listeners.forEach((l) => l()); };
const subscribe = (l) => { listeners.add(l); return () => listeners.delete(l); };
export const useScale = () => useSyncExternalStore(subscribe, () => state);
export const scaleSupported = () => typeof navigator !== "undefined" && "serial" in navigator;

const CFG_KEY = "scale_cfg";
export const getScaleConfig = () => ({ baud: 9600, dataBits: 8, parity: "none", stopBits: 1, poll: "", sim: false, preset: "generic", ...JSON.parse(localStorage.getItem(CFG_KEY) || "{}") });
export const setScaleConfig = (c) => localStorage.setItem(CFG_KEY, JSON.stringify(c));

// Generic parser for text lines like "ST,GS,+  1.234kg", "US,NT, 0.500 kg", "  1250 g", "W 1.234"
export function parseScaleLine(line) {
  const s = String(line).replace(/[\x00-\x1f]/g, " ").trim();
  if (!s) return null;
  const withUnit = s.match(/([-+]?\s*\d+(?:[.,]\d+)?)[\s,]*(kg|g)\b/i);
  const nums = s.match(/[-+]?\s*\d+(?:[.,]\d+)?/g);
  const raw = withUnit ? withUnit[1] : nums?.[nums.length - 1];
  if (!raw) return null;
  const txt = raw.replace(/\s/g, "").replace(",", ".");
  let v = Number(txt);
  if (Number.isNaN(v)) return null;
  const unit = withUnit ? withUnit[2].toLowerCase() : txt.includes(".") ? "kg" : "g";
  if (unit === "g") v /= 1000;
  const stable = /\bUS\b|unstable|\bM\b|^S\s+D\b/i.test(s) ? false : /\bST\b|stable|^S\s+S\b/i.test(s) ? true : null;
  return { kg: Math.round(v * 1000) / 1000, stable, unit: !!withUnit };
}

export function feedLine(line) {
  const txt = String(line).replace(/[\x00-\x1f]/g, " ").trim();
  if (txt) emit({ raw: [...state.raw, `${new Date().toLocaleTimeString("en-GB")}  ${txt}`].slice(-12) });
  const p = parseScaleLine(line);
  if (!p || (sawUnit && !p.unit)) return;
  if (p.unit) sawUnit = true;
  history = [...history, p.kg].slice(-3);
  const auto = history.length >= 3 && Math.max(...history) - Math.min(...history) <= 0.002;
  emit({ kg: p.kg, stable: p.stable ?? auto });
}

async function readLoop() {
  const dec = new TextDecoder();
  let buf = "";
  while (port?.readable) {
    reader = port.readable.getReader();
    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split(SPLIT);
        buf = parts.pop();
        parts.forEach(feedLine);
        if (buf.length > 200) buf = "";
      }
    } catch (e) {
      emit({ error: e.message });
    } finally {
      reader.releaseLock();
    }
  }
}

function startSim() {
  let target = 0.75;
  let kg = 0;
  let tick = 0;
  emit({ status: "connected", sim: true, error: "" });
  simTimer = setInterval(() => {
    tick += 1;
    if (tick % 30 === 0) target = Math.round((0.15 + Math.random() * 2.35) * 1000) / 1000;
    kg = Math.abs(target - kg) < 0.004 ? target : Math.round((kg + (target - kg) * 0.45) * 1000) / 1000;
    feedLine(`${kg === target ? "ST" : "US"},GS,${kg.toFixed(3)}kg`);
  }, 200);
}

async function pickPort(choose) {
  const granted = choose ? [] : await navigator.serial.getPorts();
  return granted[0] || navigator.serial.requestPort();
}

export async function connectScale(choose = false) {
  const cfg = getScaleConfig();
  if (cfg.sim) { startSim(); return; }
  if (!scaleSupported()) { emit({ status: "unsupported" }); return; }
  try {
    port = await pickPort(choose === true);
    await port.open({ baudRate: Number(cfg.baud) || 9600, dataBits: Number(cfg.dataBits) || 8, parity: cfg.parity || "none", stopBits: Number(cfg.stopBits) || 1 });
    sawUnit = false;
    emit({ status: "connected", sim: false, error: "", raw: [] });
    if (cfg.poll) pollTimer = setInterval(sendPoll, 500);
    readLoop();
  } catch (e) {
    port = null;
    emit({ status: e.name === "NotFoundError" ? "idle" : "error", error: e.message });
  }
}

export async function sendPoll() {
  const cfg = getScaleConfig();
  if (!port?.writable || !cfg.poll) return;
  try { const w = port.writable.getWriter(); await w.write(new TextEncoder().encode(unesc(cfg.poll))); w.releaseLock(); } catch (e) { /* busy */ }
}

async function sample(p, ms, poll) {
  const r = p.readable.getReader();
  const dec = new TextDecoder();
  const bytes = poll ? new TextEncoder().encode(unesc(poll)) : null;
  let buf = "";
  let ok = 0;
  const tm = setTimeout(() => r.cancel().catch(() => {}), ms);
  const pt = bytes && setInterval(async () => { try { const w = p.writable.getWriter(); await w.write(bytes); w.releaseLock(); } catch (e) { /* busy */ } }, 300);
  try {
    for (;;) {
      const { value, done } = await r.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      const parts = buf.split(SPLIT);
      buf = parts.pop();
      parts.forEach((l) => { if (l.length < 40 && parseScaleLine(l)) ok += 1; });
    }
  } catch (e) { /* timeout */ } finally { clearTimeout(tm); clearInterval(pt); r.releaseLock(); }
  return ok;
}

// Tries common speeds and data formats; keeps the one that produced most readable weight lines.
export async function autoDetectScale(onStep) {
  if (!scaleSupported()) { emit({ status: "unsupported" }); return null; }
  await disconnectScale();
  const cfg = getScaleConfig();
  let p;
  try { p = await pickPort(false); } catch (e) { return null; }
  let best = null;
  for (const baud of [9600, 4800, 2400, 19200, 1200]) {
    for (const fmt of [{ dataBits: 8, parity: "none" }, { dataBits: 7, parity: "even" }]) {
      onStep?.(`${baud} ${fmt.dataBits}${fmt.parity[0].toUpperCase()}1`);
      try { await p.open({ baudRate: baud, ...fmt, stopBits: 1 }); } catch (e) { continue; }
      const n = await sample(p, 1500, cfg.poll);
      await p.close().catch(() => {});
      if (n > (best?.n || 0)) best = { baud, ...fmt, n };
    }
  }
  if (best) setScaleConfig({ ...cfg, baud: best.baud, dataBits: best.dataBits, parity: best.parity, stopBits: 1 });
  return best;
}

export async function disconnectScale() {
  clearInterval(pollTimer);
  clearInterval(simTimer);
  const p = port;
  port = null;
  try { await reader?.cancel(); await p?.close(); } catch (e) { /* already closed */ }
  history = [];
  emit({ status: "idle", kg: 0, stable: false, sim: false });
}

export const tareScale = () => emit({ tare: state.kg });
export const clearTare = () => emit({ tare: 0 });
